import { useState, useEffect, useCallback } from 'react';
import { api, getErrorMessage } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from 'sonner';
import { 
  Package, Plus, Edit, Trash2, Search, AlertTriangle, 
  Wrench, ArrowRightLeft, Calculator, FolderTree, Building2
} from 'lucide-react';

export default function AssetManagement() {
  const [activeTab, setActiveTab] = useState('assets');
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [maintenanceRecords, setMaintenanceRecords] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialogs
  const [assetDialog, setAssetDialog] = useState(false);
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [maintenanceDialog, setMaintenanceDialog] = useState(false);
  const [transferDialog, setTransferDialog] = useState(false);
  const [depreciationDialog, setDepreciationDialog] = useState(false);
  
  // Form data
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [assetForm, setAssetForm] = useState({
    name: '', description: '', category_id: '', purchase_date: '', purchase_price: 0,
    warranty_expiry: '', location_type: 'hotel', location_name: '',
    serial_number: '', model_number: '', manufacturer: ''
  });
  const [categoryForm, setCategoryForm] = useState({
    name: '', asset_type: 'fixed', description: '', depreciation_method: 'straight_line',
    useful_life_years: 5, depreciation_rate: 20
  });
  const [maintenanceForm, setMaintenanceForm] = useState({
    asset_id: '', maintenance_type: 'preventive', description: '', scheduled_date: '', cost: 0, notes: ''
  });
  const [transferForm, setTransferForm] = useState({
    asset_id: '', to_hotel_id: '', to_location_type: 'hotel', to_location_name: '', transfer_date: '', reason: ''
  });
  
  const [depreciationReport, setDepreciationReport] = useState(null);
  const [hotels, setHotels] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [assetsRes, categoriesRes, maintenanceRes, transfersRes, hotelsRes] = await Promise.all([
        api.get('/assets'),
        api.get('/asset-categories'),
        api.get('/asset-maintenance'),
        api.get('/asset-transfers'),
        api.get('/hotels').catch(() => ({ data: [] }))
      ]);
      setAssets(assetsRes.data);
      setCategories(categoriesRes.data);
      setMaintenanceRecords(maintenanceRes.data);
      setTransfers(transfersRes.data);
      setHotels(hotelsRes.data);
    } catch (err) {
      toast.error('Failed to load assets data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateAsset = async () => {
    try {
      if (selectedAsset) {
        await api.put(`/assets/${selectedAsset.id}`, assetForm);
        toast.success('Asset updated successfully');
      } else {
        await api.post('/assets', assetForm);
        toast.success('Asset created successfully');
      }
      setAssetDialog(false);
      resetAssetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save asset');
    }
  };

  const handleCreateCategory = async () => {
    try {
      if (selectedCategory) {
        await api.put(`/asset-categories/${selectedCategory.id}`, categoryForm);
        toast.success('Category updated successfully');
      } else {
        await api.post('/asset-categories', categoryForm);
        toast.success('Category created successfully');
      }
      setCategoryDialog(false);
      resetCategoryForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleScheduleMaintenance = async () => {
    try {
      await api.post('/asset-maintenance', maintenanceForm);
      toast.success('Maintenance scheduled successfully');
      setMaintenanceDialog(false);
      setMaintenanceForm({ asset_id: '', maintenance_type: 'preventive', description: '', scheduled_date: '', cost: 0, notes: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to schedule maintenance');
    }
  };

  const handleCompleteMaintenance = async (maintenanceId) => {
    try {
      await api.put(`/asset-maintenance/${maintenanceId}/complete`, { condition: 'good' });
      toast.success('Maintenance completed');
      fetchData();
    } catch (err) {
      toast.error('Failed to complete maintenance');
    }
  };

  const handleTransferAsset = async () => {
    try {
      await api.post('/asset-transfers', transferForm);
      toast.success('Asset transferred successfully');
      setTransferDialog(false);
      setTransferForm({ asset_id: '', to_hotel_id: '', to_location_type: 'hotel', to_location_name: '', transfer_date: '', reason: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to transfer asset');
    }
  };

  const handleCalculateDepreciation = async (assetId) => {
    try {
      const res = await api.post(`/assets/${assetId}/calculate-depreciation`);
      setDepreciationReport(res.data);
      setDepreciationDialog(true);
    } catch (err) {
      toast.error('Failed to calculate depreciation');
    }
  };

  const handleDisposeAsset = async (assetId) => {
    if (!window.confirm('Are you sure you want to dispose this asset?')) return;
    try {
      await api.delete(`/assets/${assetId}?disposal_reason=End of life`);
      toast.success('Asset disposed successfully');
      fetchData();
    } catch (err) {
      toast.error('Failed to dispose asset');
    }
  };

  const resetAssetForm = () => {
    setSelectedAsset(null);
    setAssetForm({
      name: '', description: '', category_id: '', purchase_date: '', purchase_price: 0,
      warranty_expiry: '', location_type: 'hotel', location_name: '',
      serial_number: '', model_number: '', manufacturer: ''
    });
  };

  const resetCategoryForm = () => {
    setSelectedCategory(null);
    setCategoryForm({
      name: '', asset_type: 'fixed', description: '', depreciation_method: 'straight_line',
      useful_life_years: 5, depreciation_rate: 20
    });
  };

  const openEditAsset = (asset) => {
    setSelectedAsset(asset);
    setAssetForm({
      name: asset.name, description: asset.description || '', category_id: asset.category_id,
      purchase_date: asset.purchase_date?.split('T')[0] || '',
      purchase_price: asset.purchase_price, warranty_expiry: asset.warranty_expiry?.split('T')[0] || '',
      location_type: asset.location_type || 'hotel', location_name: asset.location_name || '',
      serial_number: asset.serial_number || '', model_number: asset.model_number || '',
      manufacturer: asset.manufacturer || ''
    });
    setAssetDialog(true);
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || '-';
  };

  const filteredAssets = assets.filter(a => 
    a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.asset_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6" data-testid="asset-management">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Asset Management</h1>
          <p className="text-gray-500">Track and manage hotel assets, depreciation, and maintenance</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-orange-50">
          <TabsTrigger value="assets" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
            <Package className="h-4 w-4 mr-2" /> Assets
          </TabsTrigger>
          <TabsTrigger value="categories" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
            <FolderTree className="h-4 w-4 mr-2" /> Categories
          </TabsTrigger>
          <TabsTrigger value="maintenance" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
            <Wrench className="h-4 w-4 mr-2" /> Maintenance
          </TabsTrigger>
          <TabsTrigger value="transfers" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
            <ArrowRightLeft className="h-4 w-4 mr-2" /> Transfers
          </TabsTrigger>
        </TabsList>

        {/* Assets Tab */}
        <TabsContent value="assets" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>All Assets</CardTitle>
                <CardDescription>Manage fixed assets and consumables</CardDescription>
              </div>
              <div className="flex gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search assets..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-64"
                  />
                </div>
                <Button onClick={() => { resetAssetForm(); setAssetDialog(true); }} className="bg-orange-500 hover:bg-orange-600">
                  <Plus className="h-4 w-4 mr-2" /> Add Asset
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Purchase Date</TableHead>
                    <TableHead>Purchase Price</TableHead>
                    <TableHead>Current Value</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAssets.map((asset) => (
                    <TableRow key={asset.id}>
                      <TableCell className="font-mono text-sm">{asset.asset_code}</TableCell>
                      <TableCell className="font-medium">{asset.name}</TableCell>
                      <TableCell>{getCategoryName(asset.category_id)}</TableCell>
                      <TableCell>{asset.purchase_date?.split('T')[0]}</TableCell>
                      <TableCell className="font-semibold text-orange-600">₹{asset.purchase_price?.toLocaleString()}</TableCell>
                      <TableCell className="font-semibold text-green-600">₹{asset.current_value?.toLocaleString()}</TableCell>
                      <TableCell>
                        <Badge variant={asset.status === 'active' ? 'default' : 'secondary'} 
                               className={asset.status === 'active' ? 'bg-green-500' : asset.status === 'in_maintenance' ? 'bg-yellow-500' : 'bg-red-500'}>
                          {asset.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{asset.location_name || asset.location_type}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleCalculateDepreciation(asset.id)} title="Calculate Depreciation">
                            <Calculator className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => { setMaintenanceForm({...maintenanceForm, asset_id: asset.id}); setMaintenanceDialog(true); }} title="Schedule Maintenance">
                            <Wrench className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openEditAsset(asset)} title="Edit">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDisposeAsset(asset.id)} className="text-red-500" title="Dispose">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {filteredAssets.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No assets found. Add your first asset to get started.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Asset Categories</CardTitle>
                <CardDescription>Define asset types with depreciation rules</CardDescription>
              </div>
              <Button onClick={() => { resetCategoryForm(); setCategoryDialog(true); }} className="bg-orange-500 hover:bg-orange-600">
                <Plus className="h-4 w-4 mr-2" /> Add Category
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Depreciation Method</TableHead>
                    <TableHead>Useful Life</TableHead>
                    <TableHead>Rate</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.map((cat) => (
                    <TableRow key={cat.id}>
                      <TableCell className="font-medium">{cat.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{cat.asset_type}</Badge>
                      </TableCell>
                      <TableCell>{cat.depreciation_method === 'straight_line' ? 'Straight Line' : 'WDV'}</TableCell>
                      <TableCell>{cat.useful_life_years} years</TableCell>
                      <TableCell>{cat.depreciation_rate}%</TableCell>
                      <TableCell>
                        <Badge className={cat.is_active ? 'bg-green-500' : 'bg-gray-500'}>
                          {cat.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => {
                          setSelectedCategory(cat);
                          setCategoryForm({
                            name: cat.name, asset_type: cat.asset_type, description: cat.description || '',
                            depreciation_method: cat.depreciation_method, useful_life_years: cat.useful_life_years,
                            depreciation_rate: cat.depreciation_rate
                          });
                          setCategoryDialog(true);
                        }}>
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Maintenance Tab */}
        <TabsContent value="maintenance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Maintenance Schedule</CardTitle>
              <CardDescription>Track asset maintenance and repairs</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Asset</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Scheduled</TableHead>
                    <TableHead>Cost</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {maintenanceRecords.map((m) => {
                    const asset = assets.find(a => a.id === m.asset_id);
                    return (
                      <TableRow key={m.id}>
                        <TableCell className="font-medium">{asset?.name || m.asset_id}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{m.maintenance_type}</Badge>
                        </TableCell>
                        <TableCell>{m.description}</TableCell>
                        <TableCell>{m.scheduled_date?.split('T')[0]}</TableCell>
                        <TableCell>₹{m.cost?.toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge className={
                            m.status === 'completed' ? 'bg-green-500' :
                            m.status === 'in_progress' ? 'bg-blue-500' :
                            m.status === 'scheduled' ? 'bg-yellow-500' : 'bg-gray-500'
                          }>
                            {m.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {m.status !== 'completed' && (
                            <Button variant="outline" size="sm" onClick={() => handleCompleteMaintenance(m.id)}>
                              Complete
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {maintenanceRecords.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Wrench className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No maintenance records found.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transfers Tab */}
        <TabsContent value="transfers" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Asset Transfers</CardTitle>
                <CardDescription>Track asset movements between locations</CardDescription>
              </div>
              <Button onClick={() => setTransferDialog(true)} className="bg-orange-500 hover:bg-orange-600">
                <Plus className="h-4 w-4 mr-2" /> New Transfer
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Asset</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transfers.map((t) => {
                    const asset = assets.find(a => a.id === t.asset_id);
                    return (
                      <TableRow key={t.id}>
                        <TableCell className="font-medium">{asset?.name || t.asset_id}</TableCell>
                        <TableCell>{t.from_location_name || t.from_hotel_id}</TableCell>
                        <TableCell>{t.to_location_name || t.to_hotel_id}</TableCell>
                        <TableCell>{t.transfer_date?.split('T')[0]}</TableCell>
                        <TableCell>{t.reason || '-'}</TableCell>
                        <TableCell>
                          <Badge className={t.status === 'completed' ? 'bg-green-500' : 'bg-yellow-500'}>
                            {t.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Asset Dialog */}
      <Dialog open={assetDialog} onOpenChange={setAssetDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedAsset ? 'Edit Asset' : 'Add New Asset'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Asset Name *</Label>
              <Input value={assetForm.name} onChange={(e) => setAssetForm({...assetForm, name: e.target.value})} placeholder="e.g., Reception Desk" />
            </div>
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select value={assetForm.category_id} onValueChange={(v) => setAssetForm({...assetForm, category_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Purchase Date *</Label>
              <Input type="date" value={assetForm.purchase_date} onChange={(e) => setAssetForm({...assetForm, purchase_date: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Purchase Price (₹) *</Label>
              <Input type="number" value={assetForm.purchase_price} onChange={(e) => setAssetForm({...assetForm, purchase_price: parseFloat(e.target.value) || 0})} />
            </div>
            <div className="space-y-2">
              <Label>Warranty Expiry</Label>
              <Input type="date" value={assetForm.warranty_expiry} onChange={(e) => setAssetForm({...assetForm, warranty_expiry: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Location</Label>
              <Input value={assetForm.location_name} onChange={(e) => setAssetForm({...assetForm, location_name: e.target.value})} placeholder="e.g., Lobby, Room 101" />
            </div>
            <div className="space-y-2">
              <Label>Serial Number</Label>
              <Input value={assetForm.serial_number} onChange={(e) => setAssetForm({...assetForm, serial_number: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Manufacturer</Label>
              <Input value={assetForm.manufacturer} onChange={(e) => setAssetForm({...assetForm, manufacturer: e.target.value})} />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Description</Label>
              <Textarea value={assetForm.description} onChange={(e) => setAssetForm({...assetForm, description: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssetDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateAsset} className="bg-orange-500 hover:bg-orange-600">
              {selectedAsset ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={categoryDialog} onOpenChange={setCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Category Name *</Label>
              <Input value={categoryForm.name} onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Asset Type</Label>
              <Select value={categoryForm.asset_type} onValueChange={(v) => setCategoryForm({...categoryForm, asset_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixed Asset</SelectItem>
                  <SelectItem value="consumable">Consumable</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Depreciation Method</Label>
              <Select value={categoryForm.depreciation_method} onValueChange={(v) => setCategoryForm({...categoryForm, depreciation_method: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="straight_line">Straight Line</SelectItem>
                  <SelectItem value="wdv">Written Down Value (WDV)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Useful Life (Years)</Label>
                <Input type="number" value={categoryForm.useful_life_years} onChange={(e) => setCategoryForm({...categoryForm, useful_life_years: parseInt(e.target.value) || 5})} />
              </div>
              <div className="space-y-2">
                <Label>Depreciation Rate (%)</Label>
                <Input type="number" value={categoryForm.depreciation_rate} onChange={(e) => setCategoryForm({...categoryForm, depreciation_rate: parseFloat(e.target.value) || 20})} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateCategory} className="bg-orange-500 hover:bg-orange-600">
              {selectedCategory ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Maintenance Dialog */}
      <Dialog open={maintenanceDialog} onOpenChange={setMaintenanceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule Maintenance</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Asset *</Label>
              <Select value={maintenanceForm.asset_id} onValueChange={(v) => setMaintenanceForm({...maintenanceForm, asset_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select asset" /></SelectTrigger>
                <SelectContent>
                  {assets.filter(a => a.status === 'active').map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name} ({a.asset_code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Maintenance Type</Label>
              <Select value={maintenanceForm.maintenance_type} onValueChange={(v) => setMaintenanceForm({...maintenanceForm, maintenance_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="preventive">Preventive</SelectItem>
                  <SelectItem value="corrective">Corrective</SelectItem>
                  <SelectItem value="emergency">Emergency</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Scheduled Date *</Label>
              <Input type="date" value={maintenanceForm.scheduled_date} onChange={(e) => setMaintenanceForm({...maintenanceForm, scheduled_date: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Estimated Cost (₹)</Label>
              <Input type="number" value={maintenanceForm.cost} onChange={(e) => setMaintenanceForm({...maintenanceForm, cost: parseFloat(e.target.value) || 0})} />
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea value={maintenanceForm.description} onChange={(e) => setMaintenanceForm({...maintenanceForm, description: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMaintenanceDialog(false)}>Cancel</Button>
            <Button onClick={handleScheduleMaintenance} className="bg-orange-500 hover:bg-orange-600">Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={transferDialog} onOpenChange={setTransferDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfer Asset</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Asset *</Label>
              <Select value={transferForm.asset_id} onValueChange={(v) => setTransferForm({...transferForm, asset_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select asset" /></SelectTrigger>
                <SelectContent>
                  {assets.filter(a => a.status === 'active').map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name} ({a.asset_code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Destination Hotel *</Label>
              <Select value={transferForm.to_hotel_id} onValueChange={(v) => setTransferForm({...transferForm, to_hotel_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select hotel" /></SelectTrigger>
                <SelectContent>
                  {hotels.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>New Location</Label>
              <Input value={transferForm.to_location_name} onChange={(e) => setTransferForm({...transferForm, to_location_name: e.target.value})} placeholder="e.g., Lobby, Room 201" />
            </div>
            <div className="space-y-2">
              <Label>Transfer Date *</Label>
              <Input type="date" value={transferForm.transfer_date} onChange={(e) => setTransferForm({...transferForm, transfer_date: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea value={transferForm.reason} onChange={(e) => setTransferForm({...transferForm, reason: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferDialog(false)}>Cancel</Button>
            <Button onClick={handleTransferAsset} className="bg-orange-500 hover:bg-orange-600">Transfer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Depreciation Report Dialog */}
      <Dialog open={depreciationDialog} onOpenChange={setDepreciationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Depreciation Calculation</DialogTitle>
          </DialogHeader>
          {depreciationReport && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-sm text-gray-500">Purchase Price</p>
                  <p className="text-lg font-semibold">₹{depreciationReport.purchase_price?.toLocaleString()}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-sm text-gray-500">Salvage Value</p>
                  <p className="text-lg font-semibold">₹{depreciationReport.salvage_value?.toLocaleString()}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-sm text-gray-500">Years Owned</p>
                  <p className="text-lg font-semibold">{depreciationReport.years_owned} years</p>
                </div>
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-sm text-gray-500">Method</p>
                  <p className="text-lg font-semibold">{depreciationReport.depreciation_method === 'straight_line' ? 'Straight Line' : 'WDV'}</p>
                </div>
                <div className="p-3 bg-orange-50 rounded">
                  <p className="text-sm text-orange-600">Accumulated Depreciation</p>
                  <p className="text-lg font-bold text-orange-600">₹{depreciationReport.accumulated_depreciation?.toLocaleString()}</p>
                </div>
                <div className="p-3 bg-green-50 rounded">
                  <p className="text-sm text-green-600">Current Value</p>
                  <p className="text-lg font-bold text-green-600">₹{depreciationReport.current_value?.toLocaleString()}</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setDepreciationDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
